import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, HelpCircle } from 'lucide-react'

export default function Services() {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/services')
        if (res.ok) {
          const data = await res.json()
          setServices(data)
        }
      } catch (error) {
        console.error('Fejl ved hentning af ydelser:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchServices()
  }, [])

  const processSteps = [
    {
      step: '01',
      title: 'Uforpligtende kontakt',
      description: 'Du kontakter os via telefon, e-mail eller vores formular. Vi tager en hurtig snak om din opgave.'
    },
    {
      step: '02',
      title: 'Besigtigelse & rådgivning',
      description: 'Ved større opgaver kommer vi gerne ud og kigger på opgaven, måler op og rådgiver om farver og materialer.'
    },
    {
      step: '03',
      title: 'Skriftligt tilbud',
      description: 'Du modtager et skriftligt, specificeret tilbud med en fast pris og en forventet tidsplan. Ingen skjulte gebyrer.'
    },
    {
      step: '04',
      title: 'Udførelse & aflevering',
      description: 'Vi dækker grundigt af, udfører malerarbejdet til perfektion, rydder pænt op og gennemgår resultatet sammen med dig.'
    }
  ]

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Clean Page Header (No dark background) */}
      <section className="bg-white pt-16 pb-8 border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-4 text-center space-y-3">
          <h1 className="text-3xl font-semibold sm:text-4xl lg:text-5xl text-slate-900">Vores Ydelser</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Vi tilbyder et bredt udvalg af professionelt malerarbejde i høj kvalitet. Vi dækker alt fra mindre private opgaver til store erhvervsentrepriser.
          </p>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-black border-t-transparent" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-8 max-w-md mx-auto">
              <HelpCircle className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-4 text-lg font-bold text-slate-900">Ingen ydelser fundet</h3>
              <p className="mt-2 text-sm text-slate-600">Vi opdaterer i øjeblikket vores ydelser. Kontakt os direkte for at høre mere.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="group flex flex-col overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  {/* Image */}
                  <div className="aspect-video w-full overflow-hidden bg-slate-100 relative">
                    <img
                      src={service.image?.[0]}
                      alt={service.title?.da}
                      className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="flex flex-1 flex-col p-6 sm:p-8 space-y-4">
                    <h3 className="text-xl font-semibold text-slate-900 group-hover:text-black transition-colors">
                      {service.title?.da}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed flex-1">
                      {service.description?.da}
                    </p>
                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                      <Link
                        to={`/ydelser/${service.slug?.da}`}
                        className="inline-flex items-center text-sm font-semibold text-black hover:text-slate-800 group/link"
                      >
                        Læs mere om {service.title?.da.toLowerCase()}
                        <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover/link:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Direct Contact Banner */}
          <div className="mt-16 rounded-2xl bg-slate-900 p-8 text-white text-center max-w-3xl mx-auto space-y-4 shadow-md">
            <h3 className="font-semibold text-xl">Har du en speciel maleropgave?</h3>
            <p className="text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
              Vi løser alle typer specialopgaver, der ikke er nævnt her. Kontakt os for en uforpligtende snak om dine ønsker og behov.
            </p>
            <div className="pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition-colors"
              >
                Kontakt os nu
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <h2 className="text-xs font-bold uppercase tracking-widest text-black">Vores arbejdsproces</h2>
            <p className="text-3xl font-semibold text-slate-900 sm:text-4xl">
              Sådan sikrer vi det perfekte resultat
            </p>
            <p className="text-lg text-slate-600">
              Vi tror på gennemsigtighed og struktur. Derfor følger vi altid en fastlagt proces, der sikrer, at du får præcis det resultat, du drømmer om.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((step, idx) => (
              <div key={idx} className="relative flex flex-col items-start">
                <span className="text-5xl font-light text-slate-300 mb-4 block select-none">
                  {step.step}
                </span>
                <h3 className="text-lg font-bold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
